import { COLORS, DIRS, type Cell, type Dir, type GameState } from './constants';

export interface Layout {
  originX: number;
  originY: number;
  cell: number;
  width: number;
  height: number;
}

export function layoutFor(width: number, height: number, cols: number, rows: number): Layout {
  const pad = Math.min(width, height) * 0.04;
  const cell = Math.max(12, Math.min((width - pad * 2) / cols, (height - pad * 2) / rows));
  const boardW = cell * cols;
  const boardH = cell * rows;
  return {
    originX: (width - boardW) / 2,
    originY: (height - boardH) / 2,
    cell,
    width,
    height,
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function cellCenter(layout: Layout, c: Cell, from: Cell, t: number): { x: number; y: number } {
  const x = lerp(from.x, c.x, t);
  const y = lerp(from.y, c.y, t);
  return {
    x: layout.originX + (x + 0.5) * layout.cell,
    y: layout.originY + (y + 0.5) * layout.cell,
  };
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawApple(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  golden: boolean,
  pulse: number,
): void {
  const scale = golden ? 1 + Math.sin(pulse * 8) * 0.06 : 1;
  const radius = r * scale;
  ctx.save();
  ctx.translate(x, y + 1.5);
  ctx.fillStyle = 'rgba(30, 58, 95, 0.18)';
  ctx.beginPath();
  ctx.ellipse(0, radius * 0.55, radius * 0.7, radius * 0.28, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = golden ? COLORS.gold : COLORS.apple;
  ctx.beginPath();
  ctx.arc(0, 2, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = golden ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.28)';
  ctx.beginPath();
  ctx.ellipse(-radius * 0.28, -radius * 0.15, radius * 0.28, radius * 0.18, -0.5, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = COLORS.stem;
  ctx.lineWidth = Math.max(1.5, radius * 0.18);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, -radius * 0.55);
  ctx.lineTo(0, -radius * 1.15);
  ctx.stroke();

  ctx.fillStyle = COLORS.leaf;
  ctx.beginPath();
  ctx.ellipse(radius * 0.45, -radius * 1.05, radius * 0.45, radius * 0.22, -0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawBoost(ctx: CanvasRenderingContext2D, x: number, y: number, cell: number, timeMs: number): void {
  const bounce = Math.sin(timeMs / 180) * cell * 0.04;
  ctx.save();
  ctx.translate(x, y + bounce);
  ctx.fillStyle = 'rgba(30, 58, 95, 0.16)';
  ctx.beginPath();
  ctx.ellipse(0, cell * 0.28, cell * 0.22, cell * 0.1, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = COLORS.boost;
  roundRect(ctx, -cell * 0.22, -cell * 0.28, cell * 0.44, cell * 0.5, cell * 0.12);
  ctx.fill();
  ctx.fillStyle = '#f57f17';
  ctx.beginPath();
  ctx.moveTo(cell * 0.02, -cell * 0.2);
  ctx.lineTo(-cell * 0.1, cell * 0.02);
  ctx.lineTo(cell * 0.02, cell * 0.02);
  ctx.lineTo(-cell * 0.04, cell * 0.22);
  ctx.lineTo(cell * 0.16, -cell * 0.02);
  ctx.lineTo(cell * 0.02, -cell * 0.02);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawObstacle(ctx: CanvasRenderingContext2D, layout: Layout, c: Cell): void {
  const pad = layout.cell * 0.12;
  const x = layout.originX + c.x * layout.cell + pad;
  const y = layout.originY + c.y * layout.cell + pad;
  const s = layout.cell - pad * 2;
  ctx.fillStyle = 'rgba(30, 58, 95, 0.18)';
  roundRect(ctx, x + 2, y + 3, s, s, s * 0.22);
  ctx.fill();
  ctx.fillStyle = COLORS.obstacle;
  roundRect(ctx, x, y, s, s, s * 0.22);
  ctx.fill();
  ctx.fillStyle = COLORS.obstacleTop;
  roundRect(ctx, x + s * 0.08, y + s * 0.08, s * 0.84, s * 0.38, s * 0.16);
  ctx.fill();
}

function drawPortal(
  ctx: CanvasRenderingContext2D,
  layout: Layout,
  c: Cell,
  color: string,
  timeMs: number,
): void {
  const p = cellCenter(layout, c, c, 1);
  const r = layout.cell * 0.38;
  const spin = timeMs / 400;
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.35;
  ctx.lineWidth = layout.cell * 0.08;
  ctx.beginPath();
  ctx.arc(0, 0, r * 1.15, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.rotate(spin);
  ctx.lineWidth = layout.cell * 0.1;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.72, 0.2, Math.PI * 1.4);
  ctx.stroke();
  ctx.rotate(-spin * 2);
  ctx.lineWidth = layout.cell * 0.06;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.4, 1, Math.PI * 1.6);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.35;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.22, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function segmentFrom(state: GameState, i: number): Cell {
  return state.prevSnake[i] ?? state.prevSnake[state.prevSnake.length - 1] ?? state.snake[i]!;
}

function isNear(a: { x: number; y: number }, b: { x: number; y: number }, cell: number): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy < cell * cell * 2.4;
}

function strokeSnake(
  ctx: CanvasRenderingContext2D,
  points: { x: number; y: number }[],
  width: number,
  color: string,
  offsetY = 0,
): void {
  if (points.length === 0) return;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y + offsetY);
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!;
    const cur = points[i]!;
    if (!isNear(prev, cur, width * 2)) {
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cur.x, cur.y + offsetY);
      continue;
    }
    ctx.lineTo(cur.x, cur.y + offsetY);
  }
  ctx.stroke();
}

function drawSnake(ctx: CanvasRenderingContext2D, state: GameState, layout: Layout, t: number): void {
  const points = state.snake.map((c, i) => cellCenter(layout, c, segmentFrom(state, i), t));
  if (points.length === 0) return;
  const width = layout.cell * 0.72;

  ctx.save();
  strokeSnake(ctx, points, width, COLORS.snakeShadow, 3);
  strokeSnake(ctx, points, width, COLORS.snakeDark);
  strokeSnake(ctx, points, width * 0.86, COLORS.snake);
  ctx.restore();

  const head = points[0]!;
  const dir: Dir = state.dir;
  const look = DIRS[dir];
  const eyeSpread = layout.cell * 0.16;
  const perp = { x: -look.y, y: look.x };
  const face = {
    x: head.x + look.x * layout.cell * 0.12,
    y: head.y + look.y * layout.cell * 0.12,
  };

  const eyeR = layout.cell * 0.13;
  for (const side of [-1, 1]) {
    const ex = face.x + perp.x * eyeSpread * side;
    const ey = face.y + perp.y * eyeSpread * side;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(ex, ey, eyeR, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#1c1c1c';
    ctx.beginPath();
    ctx.arc(ex + look.x * eyeR * 0.28, ey + look.y * eyeR * 0.28, eyeR * 0.48, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(ex + look.x * eyeR * 0.05 - eyeR * 0.22, ey - eyeR * 0.22, eyeR * 0.18, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function drawWorld(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  layout: Layout,
  t: number,
  timeMs: number,
): void {
  ctx.fillStyle = COLORS.grassA;
  ctx.fillRect(0, 0, layout.width, layout.height);

  const { originX, originY, cell } = layout;
  for (let y = 0; y < state.rows; y++) {
    for (let x = 0; x < state.cols; x++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? COLORS.grassA : COLORS.grassB;
      ctx.fillRect(originX + x * cell, originY + y * cell, cell + 0.5, cell + 0.5);
    }
  }

  for (const o of state.obstacles) drawObstacle(ctx, layout, o);

  if (state.portals) {
    drawPortal(ctx, layout, state.portals.a, COLORS.portalA, timeMs);
    drawPortal(ctx, layout, state.portals.b, COLORS.portalB, timeMs);
  }

  const foodFrom = state.food.cell;
  const fp = cellCenter(layout, state.food.cell, foodFrom, 1);
  const pulse = state.food.kind === 'golden' && state.food.ttlMs !== null
    ? (state.config.goldenTtlMs - state.food.ttlMs) / 1000
    : timeMs / 1000;
  drawApple(ctx, fp.x, fp.y, cell * 0.32, state.food.kind === 'golden', pulse);

  if (state.food.kind === 'golden' && state.food.ttlMs !== null) {
    const frac = Math.max(0, state.food.ttlMs / state.config.goldenTtlMs);
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(fp.x, fp.y, cell * 0.48, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2);
    ctx.stroke();
  }

  if (state.boost) {
    const bp = cellCenter(layout, state.boost.cell, state.boost.cell, 1);
    drawBoost(ctx, bp.x, bp.y, cell, timeMs);
  }

  drawSnake(ctx, state, layout, state.phase === 'playing' ? t : 0);
}
